import { EndpointSetting } from "@/constants/app/type";
import { FlightState, TypeResultSearchFlights, TypeSegmentAmenities } from "../type";
import { AMENITY_CATEGORY_MAP } from "@/utils/flight/getSegmentAmenities";
import dayjs from "dayjs";
import { getCabinClassCode } from "@/utils/flight/getCabinClassCode";
import { getCabinClassType } from "@/utils/flight/getCabinClassType";
import { filterFlights } from "@/utils/flight/filtersFlights/filterFlights";
import { getMaxShownFlights } from "@/utils/flight/getMaxShownFlight";
import { calcCountFlights } from "@/utils/flight/calcCountFlights";
import { sortCheapest } from "@/utils/flight/sortFlights/sortCheapest";
import { sortLatest } from "@/utils/flight/sortFlights/sortLatest";
import { sortEarliest } from "@/utils/flight/sortFlights/sortEarliest";
import { getAirlinesCanBeExcuted } from "@/utils/flight/getAirlinesCanBeExcuted";
import { matchFlatFarePromotion } from "@/utils/flight/matchFlatFarePromotion";
import { routesPromotionSUN } from "@/constants/flight/fixedPricesRoutes";
import { applyResultOrdering } from "@/utils/flight/sortFlights/applyResultOrdering";
import { getDatetimeSegment } from "@/utils/flight/getDatetimeSegment";
import { isGalileoSystem } from "@/utils/flight/isGalileoSystem";
import { isGalileoVjOrJqFare } from "@/utils/flight/isGalileoVjOrJqFlight";

export const callbackSearchFlights: EndpointSetting['caller'] = (
  state: FlightState,
  action: any,
  endpointName: any
) => {
  state.search_flights.loading = false
  state.search_flights.error = null
  state.search_flights.success = true
  const { fareData, airlines: airlinesData, e4t, ...result } = action.payload as TypeResultSearchFlights & { e4t?: any[] }
  const flightsResult = state.flightsResult

  // Transit ẩn (DOP-915): intermediate_stops chỉ nằm trong e4t (raw), BE lược khỏi fareData.
  // Map lại theo chữ ký segment vì flight.id không unique toàn cục (là index nội bộ mỗi fare).
  const intermediateStopsByFlightKey = new Map<string, any[]>()
  const amenitiesByFlightKey = new Map<string, TypeSegmentAmenities[]>()
  const buildFlightKey = (segments: any[], snake: boolean) => (segments || [])
    .map(s => snake
      ? `${s.flight_number}|${s.start_point}|${s.end_point}|${s.start_time}|${s.end_time}`
      : `${s.flightNumber}|${s.startPoint}|${s.endPoint}|${s.startTime}|${s.endTime}`)
    .join('::')

  // Tiện ích chuyến bay (DOP-915): campaign_label nằm ở cấp fare trong e4t (raw).
  // Mỗi nhóm dạng { "<Category>": { "<segIndex>: value": n } } - segIndex là vị trí segment.
  const parseFareAmenities = (campaignLabel: any): TypeSegmentAmenities[] => {
    const result: TypeSegmentAmenities[] = []
    if (!Array.isArray(campaignLabel)) {
      return result
    }
    for (const group of campaignLabel) {
      if (!group || typeof group !== 'object') {
        continue
      }
      const rawCat = Object.keys(group)[0]
      const field = AMENITY_CATEGORY_MAP[rawCat]
      if (!field) {
        continue
      }
      const values = group[rawCat] || {}
      for (const rawKey of Object.keys(values)) {
        const match = rawKey.match(/^(\d+):\s*(.*)$/)
        if (!match) {
          continue
        }
        const segIndex = Number(match[1])
        const value = match[2]?.trim()
        if (!value) {
          continue
        }
        if (!result[segIndex]) {
          result[segIndex] = {}
        }
        result[segIndex][field] = value
      }
    }
    return result
  }

  if (Array.isArray(e4t)) {
    for (const item of e4t) {
      // campaign_label đánh index theo toàn bộ segment của fare; cắt lại theo từng flight.
      const fareAmenities = parseFareAmenities(item?.campaign_label)
      let segOffset = 0
      for (const rawFlight of (item?.flights || [])) {
        const segCount = rawFlight?.segments?.length || 0
        if (rawFlight?.intermediate_stops?.length) {
          const key = buildFlightKey(rawFlight.segments, true)
          if (key) {
            intermediateStopsByFlightKey.set(key, rawFlight.intermediate_stops)
          }
        }
        if (fareAmenities.length && segCount) {
          const slice = fareAmenities.slice(segOffset, segOffset + segCount)
          if (slice.some(Boolean)) {
            const key = buildFlightKey(rawFlight.segments, true)
            if (key) {
              amenitiesByFlightKey.set(key, slice)
            }
          }
        }
        segOffset += segCount
      }
    }
  }
  const flightsResultLayout = flightsResult.layout
  let cheapestFlightIndex = null

  let minPrice = flightsResult.minPrice,
    maxPrice = flightsResult.maxPrice,
    maxTotalFarePrice = flightsResult.maxTotalFarePrice,
    minTotalFarePrice = flightsResult.minTotalFarePrice,
    minFarePrice = flightsResult.minFarePrice,
    maxFarePrice = flightsResult.maxFarePrice

  // store result
  const indexResult = ++flightsResult.indexResult
  flightsResult.results[indexResult] = { ...result, index: indexResult, fareData: [] }

  if (!flightsResultLayout.mappingSessionResult[result.session]) {
    flightsResultLayout.mappingSessionResult[result.session] = []
  }
  flightsResultLayout.mappingSessionResult[result.session].push(indexResult)

  if (!flightsResultLayout.mappingSessionFare[result.session]) {
    flightsResultLayout.mappingSessionFare[result.session] = []
  }

  // 
  for (let i = 0; i < fareData.length; i++) {

    // store fare data and mapping
    const { flights, ...fare } = fareData[i];

    // Skip entire Galileo fare if any leg is VJ/JQ (3 tabs: Chi tiết/Giá vé/Thông tin khác).
    // Dedicated VJ/JQ search results stay; Galileo RT must drop the whole combo.
    if (isGalileoVjOrJqFare(fare.system, flights)) {
      continue
    }

    const indexFare = ++flightsResult.indexFare
    flightsResult.results[indexResult].fareData.push(indexFare)

    flightsResultLayout.mappingSessionFare[result.session].push(indexFare)

    flightsResult.fares[indexFare] = { ...fare, index: indexFare, flights: [] }

    if (!flightsResultLayout.mappingFareFlights[indexFare]) {
      flightsResultLayout.mappingFareFlights[indexFare] = []
    }

    for (let i = 0; i < flights.length; i++) {

      // store flight data
      const flight = flights[i];
      const segments = flight.segments
      if (!flight.segments?.length) {
        continue
      }

      const firstSeg = segments[0]
      const lastSeg = segments[segments.length - 1]
      const route = `${firstSeg.startPoint}${lastSeg.endPoint}`

      const indexFlight = ++flightsResult.indexFlight
      flightsResult.fares[indexFare].flights.push(indexFlight)

      flightsResultLayout.flightIds.push(indexFlight)
      flightsResultLayout.mappingFareFlights[indexFare].push(indexFlight)
      const inventories = flight.inventories
        .sort((i1, i2) => i1.totalAmount - i2.totalAmount)
        .map(inventory => {
          const ruleNumber = getCabinClassCode(fare.system.replace('_', ''), inventory.cabinClass)
          const systemFeeAdult = fare.fees?.find(f => f.paxType === 'ADT')?.amount || 0

          const isPromotion9G = (fare.system === '9G' || fare.system === '_9G')
            && !!routesPromotionSUN[route]

          const flatFareMatch = matchFlatFarePromotion({
            airline: fare.system,
            route,
            actualPrice: inventory.totalAdt + systemFeeAdult,
            segmentCount: segments.length,
            departureTime: firstSeg.startTime,
            departureTzOffset: firstSeg.startTimeZoneOffset,
            configMap: state.flatFareConfig || {},
          })

          const isPromotionVJ = flatFareMatch.matched

          return {
            ...inventory,
            ...(isPromotionVJ ? {
              promotionVJ: true,
              promotionVJValue: flatFareMatch.config?.fareAmount,
              promotionVJDiscount: flatFareMatch.config?.totalDiscountAmount,
            } : {}),
            ...(isPromotion9G ? {
              promotionSUN: routesPromotionSUN[route].promotion
            } : {}),
            flightId: indexFlight,
            ruleNumber,
            cabinClassType: getCabinClassType(fare.system, ruleNumber)
          }
        })
        .reduce((arr: any[], inventory) => {
          if (
            arr.some((i: any) => i.cabinClass === inventory.cabinClass)
            || inventory.cabinClass === "HF"
          ) {
            return arr
          }
          return arr.concat([inventory])
        }, [])

      const firstInventory = { ...inventories[0] }
      const lastInventory = { ...inventories[inventories.length - 1] }

      // const systemFee = [...(fare?.fees || [])].reduce((total, f) => {
      //   return total + f.amount
      // }, 0)

      const systemFeeAdult = (fare?.fees || []).reduce((t: any, f: any) => {
        if (f.paxType !== 'ADULT') {
          return t
        }
        return t + f.amount
      }, 0)

      const farePrice = (firstInventory.adt * firstInventory.fareAdt) +
        (firstInventory.chd * firstInventory.fareChd) +
        (firstInventory.inf * firstInventory.fareInf)

      if (!minPrice[route]) {
        minPrice[route] = 0
      }

      if (minPrice[route] === 0 || (firstInventory.totalAmount) <= minPrice) {
        minPrice[route] = firstInventory.totalAmount
      }

      if (!minTotalFarePrice[route]) {
        minTotalFarePrice[route] = 0
      }

      if (minTotalFarePrice[route] === 0 || (firstInventory.totalAdt) <= minTotalFarePrice[route]) {
        minTotalFarePrice[route] = firstInventory.totalAdt + systemFeeAdult
      }

      if (!maxTotalFarePrice[route]) {
        maxTotalFarePrice[route] = 0
      }

      if (maxTotalFarePrice[route] === 0 || (firstInventory.totalAdt) <= maxTotalFarePrice[route]) {
        maxTotalFarePrice[route] = firstInventory.totalAdt
      }

      if (!maxPrice[route]) {
        maxPrice[route] = 0
      }

      if ((lastInventory.totalAmount) >= maxPrice[route]) {
        maxPrice[route] = lastInventory.totalAmount
      }

      // Bounds span every inventory's fareAdt: inventories are sorted by totalAmount, so first/last can disagree on fareAdt once taxes differ.
      flight.inventories.forEach((inventory) => {
        if (minFarePrice[route] == null || inventory.fareAdt < minFarePrice[route]) {
          minFarePrice[route] = inventory.fareAdt
        }
        if (maxFarePrice[route] == null || inventory.fareAdt > maxFarePrice[route]) {
          maxFarePrice[route] = inventory.fareAdt
        }
      })

      const firstSegment = flight.segments[0]

      const [_startHour] = getDatetimeSegment(firstSegment.startTime, firstSegment.startTimeZoneOffset).format('HH:mm').split(':')
      // const [_endHour, _endMin] = getDatetimeSegment(firstSegment.endTime, firstSegment.endTimeZoneOffset).format('HH:mm').split(':')

      const startHour = parseFloat(_startHour)
      let rangeTime
      if (startHour >= 0 && startHour <= 6) {
        flightsResultLayout.mappingTimeFlights.morning.push(indexFlight)
        rangeTime = 'morning'
      }

      if (startHour > 6 && startHour <= 12) {
        flightsResultLayout.mappingTimeFlights.noon.push(indexFlight)
        rangeTime = 'noon'
      }

      if (startHour > 12 && startHour <= 18) {
        flightsResultLayout.mappingTimeFlights.afternoon.push(indexFlight)
        rangeTime = 'afternoon'
      }

      if (startHour > 18 && startHour < 24) {
        flightsResultLayout.mappingTimeFlights.night.push(indexFlight)
        rangeTime = 'night'
      }

      if (cheapestFlightIndex === null || flightsResult.flights[cheapestFlightIndex].inventories[0].fareAdt > flight.inventories[0].fareAdt) {
        cheapestFlightIndex = indexFlight
      }

      const intermediateStops = isGalileoSystem(fare.system)
        ? intermediateStopsByFlightKey.get(buildFlightKey(flight.segments, false))
        : undefined

      const amenities = isGalileoSystem(fare.system)
        ? amenitiesByFlightKey.get(buildFlightKey(flight.segments, false))
        : undefined

      flightsResult.flights[indexFlight] = {
        ...flight,
        ...(intermediateStops?.length ? { intermediate_stops: intermediateStops } : {}),
        ...(amenities?.length ? { amenities } : {}),
        fareDataId: indexFare,
        session: result.session,
        inventories,
        index: indexFlight,
        rangeTime,
        segments: flight.segments.map((segment, index) => {
          let transitDuration = 0
          if (index > 0) {
            const prevSegment = flight.segments[index - 1]
            transitDuration = getDatetimeSegment(segment.startTime, segment.startTimeZoneOffset).diff(getDatetimeSegment(prevSegment.endTime, prevSegment.endTimeZoneOffset), 'minute')
          }
          return {
            ...segment,
            transitDuration
          }
        })
      }
    }
  }
  switch (flightsResult.sortBy) {
    case 'cheapest':
      flightsResultLayout.flightIds = flightsResultLayout.flightIds.sort((flightId1, flightId2) => {
        return sortCheapest(state, flightId1, flightId2)
      })
      break;
    case 'latest':
      flightsResultLayout.flightIds = flightsResultLayout.flightIds.sort((flightId1, flightId2) => {
        return sortLatest(state, flightId1, flightId2)
      })
      break;
    case 'earliest':
      flightsResultLayout.flightIds = flightsResultLayout.flightIds.sort((flightId1, flightId2) => {
        return sortEarliest(state, flightId1, flightId2)
      })
      break;

    default:
      // Mode 'default' (gợi ý): pre-sort giá thấp→cao để đúng thứ tự bên trong mỗi tier.
      flightsResultLayout.flightIds = flightsResultLayout.flightIds.sort((flightId1, flightId2) => {
        return sortCheapest(state, flightId1, flightId2)
      })
      break;
  }

  flightsResultLayout.flightIds = applyResultOrdering(state, flightsResultLayout.flightIds)

  if (cheapestFlightIndex) {
    flightsResult.flights[cheapestFlightIndex].isCheapest = true
  }


  flightsResult.filters.minPrice = minFarePrice
  flightsResult.filters.maxPrice = maxFarePrice

  flightsResult.minPrice = minFarePrice
  flightsResult.maxPrice = maxFarePrice

  flightsResult.minTotalFarePrice = minTotalFarePrice
  flightsResult.maxTotalFarePrice = maxTotalFarePrice

  flightsResult.minFarePrice = minFarePrice
  flightsResult.maxFarePrice = maxFarePrice

  const minCountShown = getMaxShownFlights()
  const countShown = flightsResultLayout.shownFlightIds?.length > minCountShown ? flightsResultLayout.shownFlightIds?.length : minCountShown
  const matchVisibleFlights = () => flightsResultLayout.flightIds
    .filter((flightId) => filterFlights(state, flightsResult.flights[flightId]))

  let flightIdsMatched = matchVisibleFlights()

  if (
    flightIdsMatched.length === 0 &&
    flightsResultLayout.flightIds.length > 0 &&
    (flightsResult.filters.classes?.length || 0) > 0
  ) {
    flightsResult.filters.classes = []
    flightIdsMatched = matchVisibleFlights()
  }

  flightsResultLayout.totalFlights = flightIdsMatched.length
  flightsResultLayout.shownFlightIds = flightIdsMatched.slice(0, countShown)

  calcCountFlights(state)
  
  if (!!airlinesData?.length) {
    for (let i = 0; i < airlinesData.length; i++) {
      const airline = airlinesData[i];
      if (!state.airlineData[airline.code]) {
        state.airlineData[airline.code] = {
          ...state.airlineData[airline.code],
          name: airline.name,
          url: `/static/images/1G/${airline.code}.png`
        }
      }

      const airlineOption = state.airlines.find(a => a.code === airline.code)
      if (!airlineOption) {
        state.airlines.push({
          code: airline.code, called: true, noExcute: true, logo: `/static/images/1G/${airline.code}.png`, name: airline.name, isDomestic: "0"
        })
      }

      localStorage.setItem('airlines', JSON.stringify(state.airlineData))
    }
  }

  state.airlines = state.airlines.map(airline => {
    if (airline.code === action.meta.arg.body.flights[0].airline) {
      return {
        ...airline,
        called: true
      }
    }
    return airline
  })
}